import { Loader2 } from "lucide-react";
import { SelectField } from "../../components/SelectField";
import { Panel } from "./fields";
import { KeyField, SaveButton, useIntegrationForm } from "./integrationForm";

const KEYS = ["PESAPAL_ENV", "PESAPAL_CONSUMER_KEY", "PESAPAL_CONSUMER_SECRET", "DPO_COMPANY_TOKEN", "DPO_SERVICE_TYPE", "DPO_API_URL", "DPO_PAY_URL",
  "CYBS_ENV", "CYBS_BANK", "CYBS_PROFILE_ID", "CYBS_ACCESS_KEY", "CYBS_SECRET_KEY", "MPGS_BANK", "MPGS_HOST", "MPGS_MERCHANT_ID", "MPGS_API_PASSWORD"];
const CYBS_BANKS = ["Absa Bank", "Co-op Bank", "KCB Bank"].map((b) => ({ value: b, label: b }));
const ENVS = [{ value: "sandbox", label: "Sandbox (testing)" }, { value: "live", label: "Live (real money)" }];

const BankGateways = ({ p, form, set }) => (
  <>
    <p className="field-label mb-3 mt-6">Bank card gateway – CyberSource (Absa, Co-op Bank, KCB)</p>
    <div className="grid gap-5 md:grid-cols-3">
      <SelectField label="Bank" value={form.CYBS_BANK || "Absa Bank"} testid="cybs-bank-select" onChange={(v) => set({ CYBS_BANK: v })} options={CYBS_BANKS} />
      <SelectField label="Environment" value={form.CYBS_ENV || "sandbox"} testid="cybs-env-select" onChange={(v) => set({ CYBS_ENV: v })} options={ENVS} />
      <KeyField label="Profile ID" name="CYBS_PROFILE_ID" {...p} testid="cybs-profile" />
      <KeyField label="Access key" name="CYBS_ACCESS_KEY" {...p} testid="cybs-access" />
      <KeyField label="Secret key" name="CYBS_SECRET_KEY" secret {...p} testid="cybs-secret" />
    </div>
    <p className="field-label mb-3 mt-6">Bank card gateway – Mastercard Gateway / MPGS (Stanbic)</p>
    <div className="grid gap-5 md:grid-cols-2">
      <KeyField label="Bank name shown to guests" name="MPGS_BANK" hint="Stanbic Bank" {...p} testid="mpgs-bank" />
      <KeyField label="Gateway address" name="MPGS_HOST" hint="test-gateway.mastercard.com" {...p} testid="mpgs-host" />
      <KeyField label="Merchant ID" name="MPGS_MERCHANT_ID" {...p} testid="mpgs-merchant" />
      <KeyField label="API password" name="MPGS_API_PASSWORD" secret {...p} testid="mpgs-password" />
    </div>
  </>
);

const Status = ({ on, name, testid }) => (
  <span className={`rounded-full px-3 py-1 text-xs ${on ? "bg-brand-light text-brand" : "bg-ink/5 text-ink/50"}`} data-testid={testid}>
    {name}: {on ? "shown at checkout" : "hidden until keys are saved"}
  </span>
);

export const CardGatewaysPanel = () => {
  const { data, form, set, save, busy } = useIntegrationForm(KEYS);
  if (!form) return <Loader2 className="h-6 w-6 animate-spin text-brand" />;
  const on = data.card_gateways || [];
  const p = { form, set };
  return (
    <Panel title="Card payments" hint="Guests pay on the gateway's or bank's secure page; card details never touch this site. Turn on as many as you like alongside M-Pesa; guests pick at checkout.">
      <div className="mb-5 flex flex-wrap gap-2">
        <Status on={on.includes("pesapal")} name="Pesapal" testid="pesapal-status" />
        <Status on={on.includes("dpo")} name="DPO Pay" testid="dpo-status" />
        <Status on={on.includes("cybersource")} name={`CyberSource (${form.CYBS_BANK || "Absa Bank"})`} testid="cybs-status" />
        <Status on={on.includes("mpgs")} name={`MPGS (${form.MPGS_BANK || "Stanbic Bank"})`} testid="mpgs-status" />
      </div>
      <p className="field-label mb-3">Pesapal</p>
      <div className="grid gap-5 md:grid-cols-3">
        <SelectField label="Environment" value={form.PESAPAL_ENV || "sandbox"} testid="pesapal-env-select" onChange={(v) => set({ PESAPAL_ENV: v })} options={ENVS} />
        <KeyField label="Consumer key" name="PESAPAL_CONSUMER_KEY" {...p} testid="pesapal-key" />
        <KeyField label="Consumer secret" name="PESAPAL_CONSUMER_SECRET" secret {...p} testid="pesapal-secret" />
      </div>
      <p className="field-label mb-3 mt-6">DPO Pay</p>
      <div className="grid gap-5 md:grid-cols-2">
        <KeyField label="Company token" name="DPO_COMPANY_TOKEN" secret {...p} testid="dpo-token" />
        <KeyField label="Service type" name="DPO_SERVICE_TYPE" hint="e.g. 3854" {...p} testid="dpo-service" />
        <KeyField label="API address (optional)" name="DPO_API_URL" hint="https://secure.3gdirectpay.com/API/v6/" {...p} testid="dpo-api-url" />
        <KeyField label="Payment page (optional)" name="DPO_PAY_URL" hint="https://secure.3gdirectpay.com/payv2.php" {...p} testid="dpo-pay-url" />
      </div>
      <BankGateways p={p} form={form} set={set} />
      <div className="mt-5 flex flex-wrap gap-3">
        <SaveButton busy={busy} onClick={() => save({ CYBS_BANK: form.CYBS_BANK || "Absa Bank", CYBS_ENV: form.CYBS_ENV || "sandbox" })} testid="card-save" />
        {on.includes("pesapal") && <button type="button" className="btn-ghost !text-red-600" disabled={busy} data-testid="pesapal-remove"
          onClick={() => window.confirm("Remove Pesapal keys?") && save({}, ["PESAPAL_CONSUMER_KEY", "PESAPAL_CONSUMER_SECRET"])}>Remove Pesapal</button>}
        {on.includes("dpo") && <button type="button" className="btn-ghost !text-red-600" disabled={busy} data-testid="dpo-remove"
          onClick={() => window.confirm("Remove DPO keys?") && save({}, ["DPO_COMPANY_TOKEN", "DPO_SERVICE_TYPE"])}>Remove DPO</button>}
        {on.includes("cybersource") && <button type="button" className="btn-ghost !text-red-600" disabled={busy} data-testid="cybs-remove"
          onClick={() => window.confirm("Remove CyberSource keys?") && save({}, ["CYBS_PROFILE_ID", "CYBS_ACCESS_KEY", "CYBS_SECRET_KEY"])}>Remove CyberSource</button>}
        {on.includes("mpgs") && <button type="button" className="btn-ghost !text-red-600" disabled={busy} data-testid="mpgs-remove"
          onClick={() => window.confirm("Remove Mastercard Gateway keys?") && save({}, ["MPGS_MERCHANT_ID", "MPGS_API_PASSWORD"])}>Remove MPGS</button>}
      </div>
    </Panel>
  );
};
